import {API_URL} from "@shared/services/api.config";
import {
    AuthGateway,
    AuthResult,
    SocialAuthResult,
} from "@domain/auth/AuthGateway";
import {
    EmailLoginCredentials,
    GoogleCallbackRequest,
    SocialLoginRequest,
} from "@domain/auth/auth";
import {AUTH_API_ERROR_CODES, AuthError, AuthErrorCode} from "@domain/auth/AuthError";
import {HttpError} from "@shared/utils/HttpError";
import {matchesErrorCode, parseApiErrorBody} from "@shared/utils/apiError";
import { ROUTES } from "@shared/routes";

export class HttpAuthGateway implements AuthGateway {
    async loginWithEmail(
        credentials: EmailLoginCredentials
    ): Promise<AuthResult> {
        let response;
        try {
            response = await fetch(`${API_URL}/api/auth/login`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(credentials),
                credentials: "include",
            });
        } catch {
            throw new AuthError(AuthErrorCode.NETWORK_ERROR);
        }

        if (response.status === 401) {
            throw new AuthError(AuthErrorCode.INVALID_CREDENTIALS);
        }

        if (response.status === 403) {
            const body = await parseApiErrorBody(response);
            if (matchesErrorCode(body.code, AUTH_API_ERROR_CODES.REGISTRATION_CLOSED)) {
                throw new AuthError(AuthErrorCode.REGISTRATION_CLOSED, body.message);
            }
        }

        if (!response.ok) {
            throw new AuthError(AuthErrorCode.NETWORK_ERROR);
        }

        return {
            authenticated: true,
        };
    }

    async logout(): Promise<AuthResult> {
        let response;
        try {
            response = await fetch(`${API_URL}/api/auth/logout`, {
                method: "POST",
                credentials: "include",
            });
        } catch {
            throw new AuthError(AuthErrorCode.NETWORK_ERROR);
        }

        if (!response.ok) {
            throw new AuthError(AuthErrorCode.LOGOUT_FAILED);
        }

        return {
            authenticated: false,
        };
    }

    async checkAuthStatus(): Promise<AuthResult> {
        let response;
        try {
            response = await fetch(`${API_URL}/api/auth/status`, {
                credentials: "include",
            });
        } catch {
            throw new AuthError(AuthErrorCode.NETWORK_ERROR);
        }

        if (response.status === 401) {
            return { authenticated: false };
        }

        if (!response.ok) {
            throw new HttpError(response.status);
        }

        const data = await response.json();
        return {
            authenticated: data.data,
        };
    }

    async initiateSocialLogin(
        request: SocialLoginRequest
    ): Promise<SocialAuthResult> {
        let response;
        try {
            const callbackUrl = `${window.location.origin}${ROUTES.authCallback}`;
            response = await fetch(
                `${API_URL}/api/auth/${
                    request.provider
                }/login?redirectUrl=${encodeURIComponent(callbackUrl)}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );
        } catch {
            throw new AuthError(AuthErrorCode.NETWORK_ERROR);
        }

        if (!response.ok) {
            throw new AuthError(AuthErrorCode.SOCIAL_LOGIN_FAILED);
        }

        const {
            data: {redirectUrl},
        } = await response.json();

        return {
            authenticated: false,
            redirectUrl,
        };
    }

    async handleGoogleCallback(
        request: GoogleCallbackRequest
    ): Promise<AuthResult> {
        let response;
        try {
            response = await fetch(`${API_URL}/api/auth/google/callback`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(request),
                credentials: "include",
            });
        } catch {
            throw new AuthError(AuthErrorCode.NETWORK_ERROR);
        }

        if (response.status === 403) {
            const body = await parseApiErrorBody(response);
            if (matchesErrorCode(body.code, AUTH_API_ERROR_CODES.REGISTRATION_CLOSED)) {
                throw new AuthError(AuthErrorCode.REGISTRATION_CLOSED, body.message);
            }
        }

        if (!response.ok) {
            throw new AuthError(AuthErrorCode.GOOGLE_LOGIN_FAILED);
        }

        const data = await response.json();
        return {
            authenticated: data.data,
        };
    }
}
